/**
 * PATH: packages/ui/src/kanban/components/card-detail/RestrictedAccessSection.tsx
 * FILE_ID: UI-KANBAN-CMP-116
 * PURPOSE: Restricted-access membership editor section.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'
import { CascadeArchiveModal } from './CascadeArchiveModal'


// =============================================================================
// RestrictedAccessSection — per-card RBAC (3f)
// -----------------------------------------------------------------------------
// Operator complaint 2026-05-08: "I can't allocate restricted
// access and structure to it [card]." When non-empty, the
// `restricted_to_user_ids` array on the card scopes which
// operators can see this single card. The default (empty array)
// means the card inherits its lane's visibility.
//
// UI surface:
//   - Compact panel below the Members section.
//   - Lock icon header + helper line ("Public" vs "Restricted to N").
//   - Multi-select chip list of restricted users + a picker button
//     to add more.
//   - The "Make public" reset button clears the entire list with
//     one click — matches the affordance operators expect from
//     Trello / Notion's "remove restriction" gesture.
// =============================================================================

export function RestrictedAccessSection({
  card,
  members,
  api,
  onChanged,
}: {
  card: Card
  members: KanbanMember[]
  api: KanbanApi
  onChanged?: (card: Card) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localIds, setLocalIds] = useState<number[]>(card.restricted_to_user_ids ?? [])
  // Re-sync when the focused card changes (drawer is reused
  // across consecutive card opens).
  useEffect(() => {
    setLocalIds(card.restricted_to_user_ids ?? [])
  }, [card.id, card.restricted_to_user_ids])

  const isRestricted = localIds.length > 0
  const memberById = useMemo(() => {
    const map = new Map<number, KanbanMember>()
    for (const m of members) map.set(m.id, m)
    return map
  }, [members])

  const persist = async (next: number[]) => {
    if (!api.restrictCardTo) return
    setBusy(true)
    setError(null)
    try {
      await api.restrictCardTo(card.id, next)
      setLocalIds(next)
      // Optimistic local update so the parent sees the new card
      // shape without waiting for a refetch.
      onChanged?.({
        ...card,
        restricted_to_user_ids: next,
      } as Card)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const candidates = members.filter((m) => !localIds.includes(m.id))

  return (
    <section>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          <ShieldAlert className="h-3 w-3" /> Restricted access
        </h3>
        <div className="flex items-center gap-1.5">
          {isRestricted && (
            <button
              type="button"
              onClick={() => void persist([])}
              disabled={busy}
              className="text-[10.5px] text-neutral-500 hover:text-neutral-800 dark:hover:text-zinc-100 disabled:opacity-50"
              title="Clear restriction — make this card visible again to every lane viewer"
            >
              Make public
            </button>
          )}
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:text-zinc-300 hover:bg-neutral-100 dark:hover:bg-zinc-900"
          >
            <UserPlus className="h-3 w-3" /> Add
          </button>
        </div>
      </div>
      <p className="mb-1 text-[11px] text-neutral-500 dark:text-zinc-400">
        {isRestricted
          ? `Visible to ${localIds.length} listed user${localIds.length === 1 ? '' : 's'} (plus the creator and board admins).`
          : 'Card inherits lane visibility (default).'}
      </p>
      {isRestricted && (
        <div className="flex flex-wrap gap-1.5">
          {localIds.map((uid) => {
            const m = memberById.get(uid)
            return (
              <span
                key={uid}
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950/30 px-2 py-0.5 text-[11px] text-amber-800 dark:text-amber-200"
              >
                <span className="truncate">{m?.name ?? `User #${uid}`}</span>
                <button
                  type="button"
                  onClick={() => void persist(localIds.filter((id) => id !== uid))}
                  disabled={busy}
                  aria-label={`Remove ${m?.name ?? 'user'}`}
                  className="rounded-full p-0.5 hover:bg-amber-200/60 dark:hover:bg-amber-900/50"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )
          })}
        </div>
      )}
      {pickerOpen && candidates.length > 0 && (
        <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-neutral-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
          {candidates.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                setPickerOpen(false)
                void persist([...localIds, m.id])
              }}
              disabled={busy}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-zinc-900 disabled:opacity-50"
            >
              <KanbanAvatar member={m} size={20} />
              <span className="truncate text-[12px] text-neutral-800 dark:text-zinc-100">{m.name}</span>
            </button>
          ))}
        </div>
      )}
      {error && (
        <p className="mt-1 text-[11px] text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------
// Stack 12 MR-C — SubCardsSection + CascadeArchiveModal
// ---------------------------------------------------------------------

/**
 * Lists the card's direct children + offers per-row actions for the
 * sub-card tree: promote a child to top-level (detach), or trigger
 * the cascade-archive flow on a child that has its own subtree.
 *
 * The section is hidden entirely when the card has zero children AND
 * the operator has not opted into "always show" mode (kept minimal:
 * cards with no sub-cards stay out of the way). We use the
 * `subtree` endpoint instead of a `cards?parent_card=` list because:
 *   1. It gives us depth info for the indent step in one round-trip.
 *   2. It already enforces tenant scope + same-board filter on the BE.
 *
 * `qc.invalidate` is scoped via `boardId` so the lane re-renders pick
 * up the new `child_count` + indent on the next render.
 */
export function SubCardsSection({
  card,
  api,
  qc,
  boardId,
  hideTitle = false,
}: {
  card: Card
  api: KanbanApi
  qc: ReturnType<typeof useQueryClient>
  boardId?: string
  /** ARC-4: suppress the inner h3 when a CollapsibleSection hosts this. */
  hideTitle?: boolean
}) {
  const hasChildren = (card.child_count ?? 0) > 0
  const q = useQuery({
    queryKey: ['kanban', 'card', card.id, 'subtree'],
    queryFn: () => api.fetchCardSubtree!(card.id),
    enabled: !!api.fetchCardSubtree && hasChildren,
  })

  const invalidateAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id, 'subtree'] })
    if (boardId) {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards', boardId] })
    } else {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards'] })
    }
  }, [boardId, card.id, qc])

  const promote = useMutation({
    mutationFn: (childId: string) => api.detachCardFromTree!(childId),
    onSuccess: invalidateAll,
  })

  const [cascadeTarget, setCascadeTarget] = useState<Card | null>(null)

  // Filter the subtree to direct children only — the row UI is one
  // level deep; nested grandchildren render under their own card's
  // SubCardsSection if the operator opens them. Strip the root.
  const directChildren = useMemo(() => {
    const all = q.data ?? []
    return all
      .filter((c) => c.parent_card === card.id && c.id !== card.id)
      .sort((a, b) =>
        (a.position_in_parent ?? a.position ?? 0)
        - (b.position_in_parent ?? b.position ?? 0),
      )
  }, [q.data, card.id])

  if (!hasChildren) return null

  return (
    <section>
      {/* ARC-4: hideTitle when hosted inside a CollapsibleSection (which
          already renders the "Sub-cards" header + count). */}
      {!hideTitle && (
        <div className="mb-1 flex items-center justify-between">
          <h3 className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
            <CornerDownRight className="h-3 w-3" /> Sub-cards
            <span className="ml-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-normal text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
              {card.child_count}
            </span>
          </h3>
        </div>
      )}
      {q.isLoading && (
        <p className="text-[11px] text-neutral-500">Loading sub-cards…</p>
      )}
      {q.isError && (
        <p className="text-[11px] text-red-600">
          Could not load sub-cards. Retry by re-opening the drawer.
        </p>
      )}
      {!q.isLoading && !q.isError && (
        <ul className="space-y-1">
          {directChildren.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-2 rounded border border-neutral-200/70 bg-neutral-50/60 px-2 py-1.5 dark:border-neutral-800/80 dark:bg-neutral-900/40"
            >
              <CornerDownRight className="h-3 w-3 shrink-0 text-neutral-400" />
              <span className="flex-1 truncate text-xs text-neutral-700 dark:text-neutral-200">
                {c.title || '(untitled)'}
              </span>
              {(c.child_count ?? 0) > 0 && (
                <span
                  className="inline-flex items-center gap-0.5 rounded bg-neutral-200 px-1 py-0.5 text-[10px] text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                  title={`${c.child_count} grandchild${c.child_count === 1 ? '' : 'ren'}`}
                >
                  <CornerDownRight className="h-2.5 w-2.5" />
                  {c.child_count}
                </span>
              )}
              {api.detachCardFromTree && (
                <button
                  type="button"
                  onClick={() => promote.mutate(c.id)}
                  disabled={promote.isPending}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 disabled:opacity-50 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
                  title="Promote this sub-card to top-level on its lane"
                  data-testid={`subcard-promote-${c.id}`}
                >
                  <ChevronUp className="h-3 w-3" /> Promote
                </button>
              )}
              {api.archiveCardSubtree && (c.child_count ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setCascadeTarget(c)}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                  title={`Archive this card and its ${c.child_count} sub-card${c.child_count === 1 ? '' : 's'}`}
                  data-testid={`subcard-archive-cascade-${c.id}`}
                >
                  <Archive className="h-3 w-3" /> Cascade…
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-[10px] text-neutral-400">
        Add a sub-card by right-clicking another card on this board and
        choosing <span className="font-medium">Make sub-card of {card.title || 'this card'}</span>.
      </p>
      {cascadeTarget && api.archiveCardSubtree && (
        <CascadeArchiveModal
          root={cascadeTarget}
          api={api}
          onClose={() => setCascadeTarget(null)}
          onArchived={() => {
            setCascadeTarget(null)
            invalidateAll()
          }}
        />
      )}
    </section>
  )
}

/**
 * Modal that fetches the subtree of a card and asks the operator to
 * confirm archiving the root + every descendant. Renders the
 * "Children: [...]" list so there's no surprise — the BE returns the
 * full subtree ordered by `tree_path` which gives us a stable
 * depth-first preview.
 *
 * Exported so other surfaces (KanbanBoardView's card context menu)
 * can reuse the same modal.
 */
